import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Terms of Service - Cynizée</title>
        <meta name="description" content="Terms of Service for Cynizée — a family task and responsibility management app. Read our full terms governing your use of the Cynizée app and services." />
        <meta name="keywords" content="terms of service, Cynizée, family app terms, child safety, parental controls, family organization" />
        <meta property="og:title" content="Terms of Service - Cynizée" />
        <meta property="og:description" content="Terms of Service for Cynizée — a family task and responsibility management app." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://cynizee.com/terms-of-service" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <div className="w-10 h-10 border-2 border-primary-foreground transform rotate-45 rounded-sm"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Effective Date: April 10, 2026
            </p>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Welcome to Cynizée. These Terms of Service govern your access to and use of the Cynizée mobile application, website, and related services. By using Cynizée, you agree to these Terms.
            </p>
            <p className="text-base text-muted-foreground mt-3 font-medium">
              If you do not agree, do not use the service.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <div className="space-y-10">

            {/* Section 1 */}
            <section id="who-we-are">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">1. Who We Are</h2>
              <p className="text-foreground leading-relaxed">
                Cynizée is a family task and responsibility management app designed to help parents and children organize chores, approvals, reminders, and related family activities.
              </p>
            </section>

            {/* Section 2 */}
            <section id="eligibility">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">2. Eligibility</h2>
              <ul className="text-foreground leading-relaxed space-y-3 list-none">
                <li>You must be at least 18 years old to create a parent account and manage a family account.</li>
                <li>Children may use Cynizée only under the supervision and authorization of a parent or legal guardian.</li>
                <li>By using the service, you represent that you have the legal authority to agree to these Terms for yourself and, where applicable, on behalf of your child.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="accounts">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">3. Accounts</h2>
              <p className="text-foreground leading-relaxed mb-2">You may submit content such as:</p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• profile information</li>
                <li>• chore details</li>
                <li>• notes</li>
                <li>• photos</li>
                <li>• shopping list items</li>
                <li>• other family-generated content</li>
              </ul>
              <p className="text-foreground leading-relaxed mb-3">
                You retain ownership of your content. By using the service, you grant us a limited license to host, store, process, and display that content only as necessary to operate and improve Cynizée.
              </p>
              <p className="text-foreground leading-relaxed">
                You are responsible for ensuring that you have the right to upload any content you submit.
              </p>
            </section>

            {/* Section 4 */}
            <section id="family-profiles">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">4. Family Profiles and Child Use</h2>
              <p className="text-foreground leading-relaxed mb-2">
                Parents may create and manage child profiles within the app. Parents are responsible for:
              </p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• the information entered for child profiles</li>
                <li>• supervising child use of the app</li>
                <li>• deciding which features, permissions, and settings are enabled for their family</li>
              </ul>
              <p className="text-foreground leading-relaxed">
                Children should use the app only as directed by a parent or guardian.
              </p>
            </section>

            {/* Section 5 */}
            <section id="acceptable-use">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">5. Acceptable Use</h2>
              <p className="text-foreground leading-relaxed mb-2">You agree not to:</p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• use the service for unlawful, abusive, or harmful purposes</li>
                <li>• interfere with or disrupt the app or its systems</li>
                <li>• attempt to gain unauthorized access to accounts, data, or services</li>
                <li>• upload false, misleading, infringing, or inappropriate content</li>
                <li>• use the service in a way that harms minors or violates another person's rights</li>
              </ul>
              <p className="text-foreground leading-relaxed">
                We may suspend or terminate access if these Terms are violated.
              </p>
            </section>

            {/* Section 6 */}
            <section id="user-content">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">6. User Content</h2>
              <p className="text-foreground leading-relaxed mb-2">You may submit content such as:</p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• profile information</li>
                <li>• chore details</li>
                <li>• notes</li>
                <li>• photos</li>
                <li>• shopping list items</li>
                <li>• other family-generated content</li>
              </ul>
              <p className="text-foreground leading-relaxed mb-3">
                You retain ownership of your content. By using the service, you grant us a limited license to host, store, process, and display that content only as necessary to operate and improve Cynizée.
              </p>
              <p className="text-foreground leading-relaxed">
                You are responsible for ensuring that you have the right to upload any content you submit.
              </p>
            </section>

            {/* Section 7 */}
            <section id="photos-media">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">7. Photos and Media</h2>
              <p className="text-foreground leading-relaxed mb-3">
                The app may allow users to upload or capture photos, including profile images and chore proof images.
              </p>
              <p className="text-foreground leading-relaxed mb-2">By uploading photos, you confirm that:</p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• you have the right to share them</li>
                <li>• they do not violate any law or third-party rights</li>
                <li>• they are appropriate for a family-oriented service</li>
              </ul>
              <p className="text-foreground leading-relaxed">
                We may remove content that violates these Terms.
              </p>
            </section>

            {/* Section 8 */}
            <section id="notifications">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">8. Notifications and Device Features</h2>
              <p className="text-foreground leading-relaxed mb-3">
                Cynizée may use device features such as notifications, camera access, photo library access, and app restriction or family-control features where supported.
              </p>
              <p className="text-foreground leading-relaxed">
                These features depend on device settings, operating system permissions, and third-party platform support. We do not guarantee uninterrupted availability of these features on every device.
              </p>
            </section>

            {/* Section 9 */}
            <section id="app-restrictions">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">9. App Restrictions and Family Controls</h2>
              <p className="text-foreground leading-relaxed mb-3">
                Some Cynizée features may help parents manage child access to apps, chores, or device behaviors.
              </p>
              <p className="text-foreground leading-relaxed mb-3">
                These features are tools for family management only. Parents are responsible for deciding how to use them and for monitoring their child's use of devices and the app.
              </p>
              <p className="text-foreground leading-relaxed">
                We do not guarantee that all restrictions or device-control features will always work perfectly on every device, operating system version, or configuration.
              </p>
            </section>

            {/* Section 10 */}
            <section id="privacy">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">10. Privacy</h2>
              <p className="text-foreground leading-relaxed">
                Your use of Cynizée is also governed by our Privacy Policy, which explains how we collect, use, and protect information.
              </p>
            </section>

            {/* Section 11 */}
            <section id="third-party">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">11. Third-Party Services</h2>
              <p className="text-foreground leading-relaxed mb-3">
                Cynizée may rely on third-party services such as cloud hosting, authentication, storage, analytics, notifications, or platform APIs.
              </p>
              <p className="text-foreground leading-relaxed">
                We are not responsible for outages, interruptions, or actions caused by third-party providers.
              </p>
            </section>

            {/* Section 12 */}
            <section id="termination">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">12. Termination</h2>
              <p className="text-foreground leading-relaxed mb-2">
                We may suspend or terminate access to the service at any time if:
              </p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4 mb-3">
                <li>• you violate these Terms</li>
                <li>• we believe your use creates risk or harm</li>
                <li>• the service is discontinued or changed</li>
              </ul>
              <p className="text-foreground leading-relaxed">
                You may stop using the service at any time.
              </p>
            </section>

            {/* Section 13 */}
            <section id="disclaimers">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">13. Disclaimers</h2>
              <p className="text-foreground leading-relaxed mb-3">
                Cynizée is provided on an "as is" and "as available" basis.
              </p>
              <p className="text-foreground leading-relaxed mb-3">
                To the fullest extent permitted by law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p className="text-foreground leading-relaxed">
                We do not guarantee that the service will be error-free, secure, or continuously available.
              </p>
            </section>

            {/* Section 14 */}
            <section id="limitation-liability">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">14. Limitation of Liability</h2>
              <p className="text-foreground leading-relaxed mb-3">
                To the fullest extent permitted by law, Cynizée and its owners, operators, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, goodwill, or business opportunity arising from or related to your use of the service.
              </p>
              <p className="text-foreground leading-relaxed">
                Our total liability for any claim relating to the service will not exceed the amount you paid us, if any, in the twelve months before the claim arose.
              </p>
            </section>

            {/* Section 15 */}
            <section id="indemnification">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">15. Indemnification</h2>
              <p className="text-foreground leading-relaxed mb-2">
                You agree to defend, indemnify, and hold harmless Cynizée and its owners, operators, and affiliates from and against claims, liabilities, damages, losses, and expenses arising from:
              </p>
              <ul className="text-foreground leading-relaxed space-y-2 ml-4">
                <li>• your use of the service</li>
                <li>• your violation of these Terms</li>
                <li>• your content</li>
                <li>• your violation of any law or rights of another person</li>
              </ul>
            </section>

            {/* Section 16 */}
            <section id="changes-terms">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">16. Changes to These Terms</h2>
              <p className="text-foreground leading-relaxed mb-3">
                We may update these Terms from time to time.
              </p>
              <p className="text-foreground leading-relaxed mb-3">
                If we make material changes, we may update the effective date and provide notice through the app or other reasonable means.
              </p>
              <p className="text-foreground leading-relaxed">
                Your continued use of the service after changes become effective means you accept the revised Terms.
              </p>
            </section>

            {/* Section 17 */}
            <section id="governing-law">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">17. Governing Law</h2>
              <p className="text-foreground leading-relaxed">
                These Terms are governed by the laws of the state or country in which the service operator is based, without regard to conflict of law principles.
              </p>
            </section>

            {/* Section 18 */}
            <section id="contact">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-3">18. Contact</h2>
              <p className="text-foreground leading-relaxed mb-4">
                If you have questions about these Terms, contact us at:
              </p>
              <div className="bg-muted rounded-xl p-6 space-y-2">
                <p className="text-foreground">
                  <span className="font-semibold">Email:</span>{' '}
                  <a href="mailto:support@cynizee.com" className="text-primary underline hover:opacity-80 transition-opacity">
                    support@cynizee.com
                  </a>
                </p>
                <p className="text-foreground">
                  <span className="font-semibold">Website:</span>{' '}
                  <a href="https://cynizee.com" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80 transition-opacity">
                    cynizee.com
                  </a>
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-12 mt-8">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-primary-foreground/10 rounded-xl flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-2 border-primary-foreground transform rotate-45 rounded-sm"></div>
            </div>
            <h3 className="text-2xl font-heading font-bold mb-4">Questions About Our Terms?</h3>
            <p className="text-primary-foreground/80 mb-6 max-w-2xl mx-auto">
              We're committed to transparency and helping families understand how Cynizée works. Don't hesitate to reach out.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 text-sm text-primary-foreground/60">
              <div>© {new Date()?.getFullYear()} Cynizée</div>
              <div>•</div>
              <div>Effective: April 10, 2026</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfService;